import React, { useState, useCallback } from 'react';
import {
  TriangleAlert as AlertTriangle,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CircleCheck as CheckCircle,
  Loader as Loader2,
  Mail,
  Clock,
  Calendar,
  PackageCheck,
  MessageSquare,
  MessageSquareText,
  ShieldAlert,
  History,
} from 'lucide-react';
import { useOrderIssues } from '../hooks/useOrderIssues';
import { useIssueEmailSchedule } from '../hooks/useIssueEmailSchedule';
import { IssueStatsCards } from '../components/orders-issue/IssueStatsCards';
import { IssueFilterBar } from '../components/orders-issue/IssueFilterBar';
import { IssueRow } from '../components/orders-issue/IssueRow';
import { IssueEmailScheduler } from '../components/orders-issue/IssueEmailScheduler';
import { IssueEmailHistory } from '../components/orders-issue/IssueEmailHistory';
import { ProcessingQueueSection } from '../components/orders-issue/ProcessingQueueSection';
import { CustomerComplaintSection } from '../components/orders-issue/CustomerComplaintSection';
import { SendIssueEmailModal } from '../components/orders-issue/SendIssueEmailModal';
import { DamageReportForm } from '../components/damage/DamageReportForm';
import { DamageHistory } from '../components/damage/DamageHistory';
import { Button } from '../components/ui/Button';
import { exportOrderIssuesToCSV, exportOrderIssuesToExcel } from '../utils/orderIssueExport';

type TabKey = 'issues' | 'schedule' | 'history' | 'under_process' | 'processed' | 'resolved' | 'customer_complaint' | 'customer_resolve_complaint' | 'report_damage' | 'damage_history';

const TABS: { key: TabKey; label: string; icon: React.FC<{ className?: string }> }[] = [
  { key: 'issues', label: 'Issues', icon: AlertTriangle },
  { key: 'schedule', label: 'Email Schedule', icon: Calendar },
  { key: 'history', label: 'Email History', icon: Clock },
  { key: 'under_process', label: 'Under Process', icon: Clock },
  { key: 'processed', label: 'Processed', icon: PackageCheck },
  { key: 'resolved', label: 'Resolved', icon: CheckCircle },
  { key: 'customer_complaint', label: 'Customer Complaint', icon: MessageSquare },
  { key: 'customer_resolve_complaint', label: 'Customer Resolve', icon: MessageSquareText },
  { key: 'report_damage', label: 'Report Damage', icon: ShieldAlert },
  { key: 'damage_history', label: 'Damage History', icon: History },
];

export const OrdersIssue: React.FC = () => {
  const {
    issues,
    loading,
    total,
    page,
    pageSize,
    filters,
    stats,
    statsLoading,
    setPage,
    updateFilters,
    loadIssues,
    loadStats,
    resolveIssue,
    ignoreIssue,
    bulkResolve,
    bulkIgnore,
  } = useOrderIssues();

  const {
    contacts,
    schedule,
    sendManualEmail,
  } = useIssueEmailSchedule();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('issues');
  const [showSendModal, setShowSendModal] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const actionableIds = issues
    .filter((i) => i.status === 'open' || i.status === 'under_process')
    .map((i) => i.id);

  const handleToggleSelectAll = useCallback(() => {
    if (actionableIds.every((id) => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(actionableIds));
    }
  }, [actionableIds, selectedIds]);

  const handleBulkResolve = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await bulkResolve(ids);
    setSelectedIds(new Set());
  }, [selectedIds, bulkResolve]);

  const handleBulkIgnore = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await bulkIgnore(ids);
    setSelectedIds(new Set());
  }, [selectedIds, bulkIgnore]);

  const handleBulkEmail = useCallback(() => {
    if (selectedIds.size === 0) return;
    setShowSendModal(true);
  }, [selectedIds]);

  const handleManualSend = useCallback(
    async (recipientIds: string[]) => {
      const issueIds = Array.from(selectedIds);
      await sendManualEmail(issueIds, recipientIds);
      setSelectedIds(new Set());
      await loadIssues();
      await loadStats();
    },
    [selectedIds, sendManualEmail, loadIssues, loadStats]
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadIssues(), loadStats()]);
    setRefreshing(false);
  }, [loadIssues, loadStats]);

  const handleExportCSV = useCallback(() => {
    exportOrderIssuesToCSV(issues);
  }, [issues]);

  const handleExportExcel = useCallback(() => {
    exportOrderIssuesToExcel(issues);
  }, [issues]);

  const allActionableSelected = actionableIds.length > 0 && actionableIds.every((id) => selectedIds.has(id));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <AlertTriangle className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Orders Issue
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Track and resolve label creation and fulfillment failures
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'issues' && (
            <>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm text-gray-700 dark:text-gray-300">
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  <button
                    onClick={handleExportCSV}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Export Excel
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {activeTab === 'issues' && (
        <IssueStatsCards stats={stats} loading={statsLoading} />
      )}

      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'issues' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700/50">
            <IssueFilterBar
              filters={filters}
              onUpdateFilters={updateFilters}
              selectedCount={selectedIds.size}
              onBulkResolve={handleBulkResolve}
              onBulkIgnore={handleBulkIgnore}
              onBulkEmail={handleBulkEmail}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
            </div>
          ) : issues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                No issues found
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
                {filters.status === 'open'
                  ? 'All orders are shipping cleanly. No open issues to resolve.'
                  : 'No issues match the current filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                    <th className="pl-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allActionableSelected}
                        onChange={handleToggleSelectAll}
                        className="h-4 w-4 text-amber-500 rounded border-gray-300 dark:border-gray-600 focus:ring-amber-500"
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Error
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                      Created
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((issue) => (
                    <IssueRow
                      key={issue.id}
                      issue={issue}
                      selected={selectedIds.has(issue.id)}
                      onToggleSelect={handleToggleSelect}
                      onResolve={resolveIssue}
                      onIgnore={ignoreIssue}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'schedule' && <IssueEmailScheduler />}

      {activeTab === 'history' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <IssueEmailHistory />
        </div>
      )}

      {activeTab === 'under_process' && (
        <ProcessingQueueSection status="under_process" onStatsChanged={handleRefresh} />
      )}

      {activeTab === 'processed' && (
        <ProcessingQueueSection status="processed" onStatsChanged={handleRefresh} />
      )}

      {activeTab === 'resolved' && (
        <ProcessingQueueSection status="resolved" />
      )}

      {activeTab === 'customer_complaint' && (
        <CustomerComplaintSection mode="create" />
      )}

      {activeTab === 'customer_resolve_complaint' && (
        <CustomerComplaintSection mode="resolved" />
      )}

      {activeTab === 'report_damage' && <DamageReportForm />}

      {activeTab === 'damage_history' && <DamageHistory />}

      {showSendModal && (
        <SendIssueEmailModal
          issueCount={selectedIds.size}
          contacts={contacts}
          defaultRecipientIds={schedule?.recipient_ids || []}
          onConfirm={handleManualSend}
          onClose={() => setShowSendModal(false)}
        />
      )}
    </div>
  );
};
